import { paymentMethods, type PaymentMethod } from './enums.js';

/**
 * Moyens cochés dans Paramètres → Facturation.
 *
 * Une seule liste. `paymentMethods` reste l'ensemble fermé de la base et des encaissements déjà
 * écrits : Wero et Payconiq y restent pour qu'une pièce ancienne se relise. Ils ne se cochent plus
 * ici. Wero suit l'interrupteur de Paramètres → Wero ; Payconiq n'est plus proposé.
 */
export const manualPaymentMethods = [
  'cash',
  'card',
  'transfer',
  'sumup',
  'other',
] as const satisfies readonly PaymentMethod[];

type ManualPaymentMethod = (typeof manualPaymentMethods)[number];
type HiddenFromSettings = Exclude<PaymentMethod, ManualPaymentMethod>;
type ExpectedHidden = 'wero' | 'payconiq';

/** Échoue à la compilation si l'ensemble fermé gagne un moyen qui n'est ni manuel, ni Wero, ni Payconiq. */
export type PaymentMethodCatalogAgrees = [HiddenFromSettings] extends [ExpectedHidden]
  ? [ExpectedHidden] extends [HiddenFromSettings]
    ? true
    : never
  : never;

const manualSet = new Set<PaymentMethod>(manualPaymentMethods);

/**
 * Moyens encore saisissables à la main. Payconiq n'y figure pas. Wero n'y figure que si
 * l'intégration est active — jamais parce qu'une ancienne case l'avait coché.
 */
export function recordablePaymentMethods(weroAccepted: boolean): PaymentMethod[] {
  return paymentMethods.filter((method) => {
    if (method === 'wero') return weroAccepted;
    return manualSet.has(method);
  });
}

/**
 * Boutons d'encaissement en accès direct : les moyens manuels cochés, plus Wero si l'intégration
 * est active. Une valeur `wero` ou `payconiq` encore stockée dans les réglages ne compte pas.
 */
export function offeredPaymentMethods(
  stored: readonly PaymentMethod[],
  weroAccepted: boolean,
): PaymentMethod[] {
  const checked = new Set<PaymentMethod>(stored);
  return recordablePaymentMethods(weroAccepted).filter(
    (method) => method === 'wero' || checked.has(method),
  );
}

/** Cases affichées comme cochées. `payconiq` et `wero` stockés sont ignorés. */
export function configurablePaymentMethods(stored: readonly PaymentMethod[]): PaymentMethod[] {
  const checked = new Set<PaymentMethod>(stored);
  return manualPaymentMethods.filter((method) => checked.has(method));
}
